import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { getNameInitials } from '@/utils/affiliatePromotionLinkHelpers';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AffiliateReportProduct } from '@/@types/AffiliateReport';

interface Props {
  products: AffiliateReportProduct[];
  affiliateName: string;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (productId: string) => void;
}

export default function AffiliateProductPickerDialog({
  products,
  affiliateName,
  isOpen,
  onClose,
  onConfirm,
}: Props) {
  const { t } = useTranslation('reports');
  const [selectedProductId, setSelectedProductId] = useState<string | null>(
    products[0]?.productId ?? null,
  );

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
      setSelectedProductId(products[0]?.productId ?? null);
    }
  };

  const handleConfirm = () => {
    if (selectedProductId) {
      onConfirm(selectedProductId);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md w-[calc(100%-2rem)] mb-16">
        <DialogHeader>
          <DialogTitle>{t('affiliateReport.productPicker.title')}</DialogTitle>
          <p className="text-sm text-zinc-400">
            {t('affiliateReport.productPicker.description', {
              name: affiliateName,
            })}
          </p>
        </DialogHeader>

        <div className="space-y-2 max-h-[280px] overflow-y-auto py-2">
          {products.map((product) => (
            <button
              key={product.productId}
              type="button"
              onClick={() => setSelectedProductId(product.productId)}
              className={cn(
                'w-full flex items-center gap-3 text-left p-3 rounded-lg border transition-colors',
                selectedProductId === product.productId
                  ? 'border-primary bg-primary/10'
                  : 'border-zinc-800 hover:border-zinc-600',
              )}
            >
              <Avatar className="h-10 w-10 shrink-0 rounded-md">
                {product.imageUrl && (
                  <AvatarImage
                    src={product.imageUrl}
                    alt={product.productName}
                    className="rounded-md object-cover"
                  />
                )}
                <AvatarFallback className="rounded-md bg-zinc-800 text-zinc-300 text-xs">
                  {getNameInitials(product.productName)}
                </AvatarFallback>
              </Avatar>
              <span className="text-sm font-medium">{product.productName}</span>
            </button>
          ))}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t('affiliateReport.productPicker.cancel')}
          </Button>
          <Button onClick={handleConfirm} disabled={!selectedProductId}>
            {t('affiliateReport.productPicker.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
